// Unified data service. Phase 35b/c/d: components call this instead of
// the raw `api` client.
//
// Write strategy (Phase 35d):
//   1. Write to IndexedDB first (instant UI, sub-10ms).
//   2. If Google is connected, push to server in the background (fire-and-forget).
//   3. If not connected, data stays local-only. Phase 35e sync engine
//      will push pending writes when connection is established.
//
// Read strategy:
//   - All reads come from IndexedDB (instant, offline-capable).
//   - The server is no longer in the read path for daily use.
//
// The previous server-as-source-of-truth model has been replaced. The
// server still validates per-user scoping when we DO push (so the cookie
// + user_id are still checked), but reads never hit it.

import {
  categoryRepo, settingsRepo, txRepo, budgetRepo, recurringRepo, cardRepo, goalRepo,
  accountRepo, getMeta, wipeLocalAccount,
  normalizeReminderEnabled, normalizeReminderDaysBefore,
  REMINDER_DEFAULT_DAYS_BEFORE, REMINDER_FALLBACK_ENABLED,
} from './localDb.js';
// Phase 208 (card 208-B4): the key name and the value encoding for the
// global recurring-reminder opt-in. This module is PURE (no imports of
// its own), so importing it here creates no cycle.
import {
  RECURRING_REMINDER_SETTING_KEY, recurringReminderEnabledValue,
  isRecurringReminderEnabled,
} from './recurringReminderSetting.js';
import { api } from '../api/client.js';

// Returns true if the user has connected a Google account. Used to
// decide whether to push writes to the server right away.
async function isConnected() {
  const sub = await getMeta('connected_google_sub');
  return !!sub;
}

// Fire-and-forget server push. Logs but does NOT throw — local write
// already succeeded, so the user's data is safe even if the network
// call fails. The Phase 35e sync engine retries unsynced rows on its
// own schedule.
//
// row: the local row that was just inserted/updated. For creates, it
// has local_uuid + no server_id. For updates, it has server_id.
//
// Phase 202: also capture the server's response and, on a successful
// POST, write the returned `id` back to the local row's `server_id`.
// Without this, every locally-created row was forever stuck with
// `server_id: null`, which made updateTransaction and
// deleteTransaction (Phase 35d write paths) silently skip the
// push — they both gate on `if (row.server_id)`. The fix is one
// post-success step here, applied uniformly to all 7 tables.
async function pushInBackground(tableName, localRow, method, path, body) {
  if (!await isConnected()) return; // local-only mode: skip
  try {
    const resp = await api[method](path, body);
    // Phase 202: capture server_id from POST responses. PATCH/PUT/DELETE
    // don't return the row, so they don't change server_id. The server
    // returns the full row on POST with `id` as the integer PK; that
    // id IS the server_id. If the response is missing `id` (PATCH or
    // unexpected shape), skip — we just won't update server_id.
    if (
      method === 'post'
      && localRow && localRow.local_uuid
      && resp && typeof resp === 'object' && resp.id != null
      && localRow.server_id == null
    ) {
      try {
        const { db, tableKey } = await resolveTable(tableName);
        if (db && tableKey) {
          await db[tableKey].update(localRow.local_uuid, { server_id: resp.id });
        }
      } catch (applyErr) {
        // If applying the server_id fails (Dexie closed, schema drift),
        // log but don't crash. The local row is fine; the next sync
        // attempt will see `server_id: null` and the row will be
        // re-considered dirty. Worst case: a duplicate on the server
        // (which the user will see and can clean up). Don't throw.
        console.warn(`[dataService] failed to apply server_id for ${tableName} (${localRow.local_uuid}):`, applyErr?.message || applyErr);
      }
    }
  } catch (e) {
    // Phase 35e: enqueue transient failures for retry. Permanent
    // failures (4xx other than 408/429) are dropped — they indicate
    // a logic problem, not a network blip, and the local write
    // succeeded so the user can keep working.
    const { enqueue, isPermanentFailure } = await import('./syncQueue.js');
    const targetLocalUuid = localRow && localRow.local_uuid;
    if (isPermanentFailure(e)) {
      console.warn(`[dataService] permanent push failure for ${tableName} (status=${e.status || 'unknown'}): ${e.message}`);
      return;
    }
    try {
      await enqueue({ table: tableName, targetLocalUuid, method, path, body, error: e });
      console.warn(`[dataService] enqueued ${tableName} push for retry (${e.message})`);
    } catch (enqueueErr) {
      // If even the queue write fails (disk full?), fall back to
      // logging so we have something in the console.
      console.warn(`[dataService] background push + enqueue both failed for ${tableName}`, e.message, enqueueErr.message);
    }
  }
}

// Phase 202: map the dataService table-name to the Dexie table key.
// dataService names use singular camelCase (e.g. 'creditCards') while
// the Dexie schema uses plural snake_case (e.g. 'credit_cards'). This
// small helper keeps the mapping in one place so future tables only
// need to be added here.
async function resolveTable(tableName) {
  const map = {
    transactions:  { db: (await import('./localDb.js')).db, tableKey: 'transactions' },
    categories:    { db: (await import('./localDb.js')).db, tableKey: 'categories' },
    settings:      { db: (await import('./localDb.js')).db, tableKey: 'settings' },
    budgets:       { db: (await import('./localDb.js')).db, tableKey: 'budgets' },
    recurring:     { db: (await import('./localDb.js')).db, tableKey: 'recurring' },
    creditCards:   { db: (await import('./localDb.js')).db, tableKey: 'credit_cards' },
    goals:         { db: (await import('./localDb.js')).db, tableKey: 'goals' },
    accounts:      { db: (await import('./localDb.js')).db, tableKey: 'accounts' },
  };
  return map[tableName] || { db: null, tableKey: null };
}

// Read helpers — IndexedDB-backed, instant.
//
// Test escape hatch (M-76): if `globalThis.__financeAppTestData__` is
// set, reads go through that mock. Production runs never set the key,
// so the default IndexedDB-backed implementations are used.
//
// Implementation: a Proxy that delegates property access to whichever
// backing object is current — the test mock if set, otherwise the
// real repo-backed object. This lets tests swap the mock mid-suite
// (e.g. before a single test) without re-importing this module.
const REAL_DATA = {
  async getCategories() { return categoryRepo.list(); },
  // Phase 83: Settings page needs to see disabled categories too (so the
  // user can re-enable them). All other pages use getCategories() which
  // hides disabled categories from pickers + filter dropdowns.
  async getCategoriesAll() { return categoryRepo.listAll(); },
  async getSettings() { return settingsRepo.getAll(); },
  // Phase 208 (card 208-B4): the global opt-in for recurring-transaction
  // reminders. Reads the generic `recurring_reminder_enabled` key from
  // the local settings table — the SAME read path as every other
  // setting (local-first; the server is never in the read path).
  // An absent key reads as OFF, which is the defined default: the
  // feature stays off until the user explicitly opts in.
  //
  // `settingsRepo.get(key)` reads the one row rather than scanning the
  // whole table — this is called on every dashboard mount.
  //
  // The semantics live in `recurringReminderSetting.js` so the
  // dashboard effect (card 208-A3) and the settings toggle (card 208-A2)
  // cannot disagree about what "on" means.
  async getRecurringReminderEnabled() {
    const value = await settingsRepo.get(RECURRING_REMINDER_SETTING_KEY);
    return isRecurringReminderEnabled({ [RECURRING_REMINDER_SETTING_KEY]: value });
  },
  async getTransactions(opts) { return txRepo.list(opts || {}); },
  async getBudgets() { return budgetRepo.list(); },
  async getRecurring() { return recurringRepo.list(); },
  // Phase 109b: dashboard calls this once per mount to auto-apply any
  // pending monthly recurring rules. Returns inserted tx rows so the
  // dashboard can show a banner.
  async applyRecurringPending() { return recurringRepo.applyPending(); },
  async getCreditCards() { return cardRepo.list(); },
  async getGoals() { return goalRepo.list(); },
  // Phase 203: accounts are local-only (no server endpoint). This was
  // MISSING before — AddTransactionModal called `data.getAccounts()` and
  // the optional-chaining guard silently swallowed the miss, so the
  // account picker always rendered empty.
  async getAccounts() { return accountRepo.list(); },
};

function pickData() {
  return (typeof globalThis !== 'undefined' && globalThis.__financeAppTestData__)
    || REAL_DATA;
}
export const data = new Proxy({}, {
  get(_t, key) {
    const target = pickData();
    const v = target[key];
    return typeof v === 'function' ? v.bind(target) : v;
  },
  has(_t, key) { return key in pickData(); },
});

// Write helpers — IndexedDB-first. Returns the local row immediately.
// If connected, also pushes to server in the background.
const REAL_WRITES = {
  // ── Transactions ──
  async createTransaction(input) {
    const row = await txRepo.add(input);
    pushInBackground('transactions', row, 'post', '/transactions', stripLocalFields(input));
    return row;
  },
  async updateTransaction(local_uuid, patch) {
    const row = await txRepo.update(local_uuid, patch);
    if (!row) return null;
    if (row.server_id) {
      pushInBackground('transactions', row, 'patch', `/transactions/${row.server_id}`, stripLocalFields(patch));
    }
    return row;
  },
  async deleteTransaction(local_uuid) {
    const row = await txRepo.get(local_uuid);
    if (!row) return;
    await txRepo.remove(local_uuid);
    if (row.server_id) {
      pushInBackground('transactions', row, 'delete', `/transactions/${row.server_id}`);
    }
  },
  // Phase 78: bulk soft-delete via txRepo.bulkRemove (single Dexie
  // transaction). Then push per-row DELETE to the server for any row
  // that has already been synced. Returns the count actually deleted.
  async bulkDeleteTransactions(local_uuids) {
    const rows = await txRepo.bulkRemove(local_uuids);
    for (const row of rows) {
      if (row.server_id) {
        pushInBackground('transactions', row, 'delete', `/transactions/${row.server_id}`);
      }
    }
    return rows.length;
  },
  // Phase 103: Reset All Settings — wipe the entire local IDB and
  // reload. Mirrors the existing `wipeLocalAccount()` helper in
  // localDb.js but exposed via the writes API so the Settings page
  // can call it through the standard interface.
  //
  // Server-side data is NOT touched (matches wipeLocalAccount). After
  // reload, _ensureSeededInFlight re-seeds the defaults (categories,
  // cash account, sample _meta) and user_prefs.onboarding stays null
  // so the onboarding carousel re-fires.
  async wipeAllLocalData() {
    return wipeLocalAccount();
  },
  async wipeAllTransactions() {
    // Capture existing server_ids BEFORE clearing.
    const rows = await txRepo.list({});
    await txRepo.wipe();
    if (await isConnected()) {
      pushInBackground('transactions', null, 'delete', '/transactions');
    }
    return rows.length;
  },

  // ── Categories ──
  async createCategory(input) {
    const row = await categoryRepo.add(input);
    pushInBackground('categories', row, 'post', '/categories', stripLocalFields(input));
    return row;
  },
  async updateCategory(local_uuid, patch) {
    const row = await categoryRepo.update(local_uuid, patch);
    if (!row) return null;
    if (row.server_id) {
      pushInBackground('categories', row, 'patch', `/categories/${row.server_id}`, stripLocalFields(patch));
    }
    return row;
  },
  async deleteCategory(local_uuid) {
    const row = await categoryRepo.get(local_uuid);
    if (!row) return;
    await categoryRepo.remove(local_uuid);
    if (row.server_id) {
      pushInBackground('categories', row, 'delete', `/categories/${row.server_id}`);
    }
  },
  // Phase 79f: toggle a default category's disabled flag. Mirror of
  // the server's PATCH /categories/:id/toggle endpoint.
  async toggleCategory(local_uuid, disabled) {
    const row = await categoryRepo.get(local_uuid);
    if (!row) return null;
    await categoryRepo.setDisabled(local_uuid, disabled);
    if (row.server_id) {
      pushInBackground('categories', row, 'patch', `/categories/${row.server_id}/toggle`, { disabled: !!disabled });
    }
    return categoryRepo.get(local_uuid);
  },
  async reorderCategories(ordered) {
    // ordered: [{ local_uuid, sort_order, type }]
    await categoryRepo.reorder(ordered);
    if (await isConnected()) {
      // Server still wants `order` with numeric category_id. Map local → server id.
      const rows = await categoryRepo.list();
      const byLocal = new Map(rows.map(r => [r.local_uuid, r]));
      const payload = ordered.map(o => {
        const full = byLocal.get(o.local_uuid);
        return full?.server_id
          ? { id: full.server_id, sort_order: o.sort_order, type: o.type }
          : null;
      }).filter(Boolean);
      pushInBackground('categories', null, 'put', '/categories/reorder', { order: payload });
    }
  },

  // ── Settings ──
  async setSetting(key, value) {
    await settingsRepo.set(key, value);
    if (await isConnected()) {
      pushInBackground('settings', null, 'put', `/settings/${key}`, { value });
    }
  },

  // Phase 208 (card 208-B4): write the global opt-in for recurring-
  // transaction reminders. Delegates to the generic `setSetting` path so
  // the value encoding ('true' / 'false' strings) is decided in ONE
  // place — `recurringReminderSetting.js` — and the server push rides
  // along automatically (the settings table is a KV table on both sides,
  // so no per-key plumbing is needed).
  async setRecurringReminderEnabled(enabled) {
    const value = recurringReminderEnabledValue(enabled);
    await REAL_WRITES.setSetting(RECURRING_REMINDER_SETTING_KEY, value);
    return value;
  },

  // ── Budgets ──
  async setBudget(category_id, monthly_limit) {
    const row = await budgetRepo.set(category_id, monthly_limit);
    // Server route is keyed by numeric category_id — map from local uuid.
    const cat = await categoryRepo.get(category_id);
    const serverCatId = cat?.server_id;
    if (serverCatId) {
      pushInBackground('budgets', row, 'put', `/budgets/${serverCatId}`, { monthly_limit });
    }
    return row;
  },
  async deleteBudget(category_id) {
    const cat = await categoryRepo.get(category_id);
    const serverCatId = cat?.server_id;
    await budgetRepo.remove(category_id);
    if (serverCatId) {
      pushInBackground('budgets', null, 'delete', `/budgets/${serverCatId}`);
    }
  },

  // ── Recurring ──
  async createRecurring(rule) {
    const row = await recurringRepo.add(rule);
    // Phase 208-B3: build the payload from the SAVED row, not the raw
    // input. `recurringRepo.add()` normalises the two reminder fields
    // (reminder_enabled / reminder_days_before) and supplies their
    // defaults — the input may not carry them at all. Pushing the input
    // would therefore send a payload that disagrees with what is
    // actually stored in IndexedDB.
    const payload = await recurringPayloadForServer(row);
    if (payload) pushInBackground('recurring', row, 'post', '/recurring', payload);
    return row;
  },
  async updateRecurring(local_uuid, patch) {
    const row = await recurringRepo.update(local_uuid, patch);
    if (!row) return null;
    if (row.server_id) {
      const payload = await recurringPayloadForServer(row);
      pushInBackground('recurring', row, 'put', `/recurring/${row.server_id}`, payload);
    }
    return row;
  },
  async deleteRecurring(local_uuid) {
    const row = await recurringRepo.get(local_uuid);
    if (!row) return;
    await recurringRepo.remove(local_uuid);
    if (row.server_id) {
      pushInBackground('recurring', row, 'delete', `/recurring/${row.server_id}`);
    }
  },

  // ── Credit cards ──
  async createCard(card) {
    const row = await cardRepo.add(card);
    pushInBackground('credit_cards', row, 'post', '/credit-cards', stripLocalFields(card));
    return row;
  },
  async updateCard(local_uuid, patch) {
    const row = await cardRepo.update(local_uuid, patch);
    if (!row) return null;
    if (row.server_id) {
      pushInBackground('credit_cards', row, 'put', `/credit-cards/${row.server_id}`, stripLocalFields(patch));
    }
    return row;
  },
  async deleteCard(local_uuid) {
    const row = await cardRepo.get(local_uuid);
    if (!row) return;
    await cardRepo.remove(local_uuid);
    if (row.server_id) {
      pushInBackground('credit_cards', row, 'delete', `/credit-cards/${row.server_id}`);
    }
  },

  // ── Goals (Phase 48) ──
  // Local-only writes for now. Q3 user pick 2026-08-25: server push is
  // stubbed — `pushInBackground` is a no-op when `goalPathForServer`
  // returns null. The full server route (POST /goals, PATCH /goals/:id,
  // DELETE /goals/:id) is a Phase 48b follow-up.
  async createGoal(input) {
    const row = await goalRepo.add(input);
    // Phase 48: no server push yet. When wired, the server route will
    // mirror the budgets pattern (POST /goals, expects numeric
    // category_id; here we send linked_category_id as a local_uuid and
    // map it server-side).
    const serverPayload = await goalPayloadForServer(row);  // BUGFIX: must await
    if (serverPayload) pushInBackground('goals', row, 'post', '/goals', serverPayload);
    return row;
  },
  async updateGoal(local_uuid, patch) {
    const row = await goalRepo.update(local_uuid, patch);
    if (!row) return null;
    const serverPayload = await goalPayloadForServer(row);  // BUGFIX
    if (serverPayload && row.server_id) {
      pushInBackground('goals', row, 'put', `/goals/${row.server_id}`, serverPayload);
    }
    return row;
  },
  async deleteGoal(local_uuid) {
    const row = await goalRepo.get(local_uuid);
    if (!row) return;
    await goalRepo.remove(local_uuid);
    if (row.server_id) {
      pushInBackground('goals', null, 'delete', `/goals/${row.server_id}`);
    }
  },
};
function pickWrites() {
  return (typeof globalThis !== 'undefined' && globalThis.__financeAppTestWrites__) || REAL_WRITES;
}
export const writes = new Proxy({}, {
  get(_t, key) {
    const target = pickWrites();
    const v = target[key];
    return typeof v === 'function' ? v.bind(target) : v;
  },
  has(_t, key) { return key in pickWrites(); },
});;

// Strip local-only fields before sending to the server. Server doesn't
// know about local_uuid, server_id, or the deleted flag (server uses
// its own FK delete semantics).
function stripLocalFields(obj) {
  if (!obj) return obj;
  const out = {};
  // Fields the server doesn't know about / shouldn't see:
  //   - local_uuid / server_id / deleted: standard local-only metadata
  //   - credit_card_local_uuid: Phase 49e — the canonical local
  //     reference for the card, but the server uses the numeric FK
  //     in `credit_card_id` instead. Sending both is harmless but
  //     keeps the API payload minimal.
  const LOCAL_ONLY = new Set(['local_uuid', 'server_id', 'deleted', 'credit_card_local_uuid']);
  for (const [k, v] of Object.entries(obj)) {
    if (LOCAL_ONLY.has(k)) continue;
    out[k] = v;
  }
  return out;
}

// Export helpers for testing (Phase 136). Pure functions, no runtime cost
// beyond a one-time registration.
export const __syncHelpers = {
  stripLocalFields,
  // Phase 208-B3: exported so the recurring sync payload can be asserted
  // directly (the reminder fields must survive the local→server mapping).
  recurringPayloadForServer,
};

// Recurring rules store category_id as local_uuid; server expects the
// numeric id. Map it before pushing.
async function recurringPayloadForServer(rule) {
  // BUGFIX: same null-safety as applyPending in localDb.js — if
  // category_id is null (user didn't link a category, or it was
  // deleted), Dexie.get(null) throws "Invalid argument". Guard
  // explicitly. Goal payload uses the same shape.
  if (!rule.category_id) return null;
  const cat = await categoryRepo.get(rule.category_id);
  if (!cat || !cat.server_id) return null; // can't push if no server id yet
  // Phase 208-B3: `stripLocalFields` is a denylist (local_uuid /
  // server_id / deleted / credit_card_local_uuid), so the two reminder
  // fields already ride along in the spread. They are listed
  // EXPLICITLY here anyway so the contract is visible at the call site
  // and a future whitelist-style stripLocalFields cannot silently drop
  // them — and so the pushed values are always integers.
  //
  // The fallback is 0 (OFF), not the new-rule default of 1: this function
  // may be handed a row that predates Phase 208, and such a rule must not
  // start producing reminders because it was re-pushed to the server.
  return {
    ...stripLocalFields(rule),
    category_id: cat.server_id,
    reminder_enabled: normalizeReminderEnabled(rule.reminder_enabled, REMINDER_FALLBACK_ENABLED),
    reminder_days_before: normalizeReminderDaysBefore(rule.reminder_days_before, REMINDER_DEFAULT_DAYS_BEFORE),
  };
}

// Goals: same local→server id mapping for linked_category_id. Returns
// null when the category hasn't been pushed yet (no server_id), which
// suppresses the background push. Phase 48 ships local-only; Phase 48b
// wires the server route.
async function goalPayloadForServer(goal) {
  if (!goal.linked_category_id) {
    return stripLocalFields(goal);
  }
  const cat = await categoryRepo.get(goal.linked_category_id);
  if (!cat || !cat.server_id) return null;
  return {
    ...stripLocalFields(goal),
    linked_category_id: cat.server_id,
  };
}

// Legacy escape hatch — components that still want raw server access
// can `import { api } from '../api/client.js'` directly.
export { api };